/** Flesh and kit: hits, grunts, falls, breathing, first aid, pickups, the time bomb. */

import { PRIORITY, type SfxDef } from './types';
import { syllable } from './voice';

export const BODY_SFX = {
  rangerHit: {
    variants: 3, priority: PRIORITY.action, gain: 0.7, rateSpread: 0.05,
    patch: {
      layers: [
        { wave: 'noise', decay: 0.045, curve: 3, gain: 1, filter: { type: 'lp', cutoff: 1600 } },
        { wave: 'sine', freq: 210, freqEnd: 90, decay: 0.07, gain: 0.9 },
        ...syllable({ at: 0.03, dur: 0.18, from: 155, to: 110, vowel: 'uh', attack: 0.006, breath: 0.3, gain: 0.9 }),
      ],
      jitter: { pitch: 0.05, time: 0.08 },
    },
  },
  rangerWounded: {
    variants: 2, priority: PRIORITY.critical, gain: 0.8,
    patch: {
      layers: [
        { wave: 'noise', decay: 0.06, curve: 3, gain: 1, filter: { type: 'lp', cutoff: 1300 } },
        { wave: 'sine', freq: 180, freqEnd: 60, decay: 0.12, gain: 1 },
        ...syllable({ at: 0.04, dur: 0.36, from: 175, to: 92, vowel: 'ah', attack: 0.006, breath: 0.4, vib: 0.3 }),
      ],
      jitter: { pitch: 0.04, time: 0.06 }, drive: 1.3,
    },
  },
  rangerDowned: {
    variants: 1, priority: PRIORITY.critical, gain: 0.8,
    patch: {
      layers: [
        ...syllable({ at: 0, dur: 0.7, from: 145, to: 70, vowel: 'oh', attack: 0.02, breath: 0.4, vib: 0.4 }),
        { wave: 'sine', at: 0.5, freq: 84, freqEnd: 34, sweep: 0.6, decay: 0.26, gain: 1 },
        { wave: 'noise', at: 0.5, decay: 0.2, curve: 2.5, gain: 0.8, filter: { type: 'lp', cutoff: 520 } },
        { wave: 'noise', at: 0.56, decay: 0.12, gain: 0.3, filter: { type: 'bp', cutoff: 2400, q: 1.5 } },
      ],
    },
  },
  enemyHit: {
    variants: 3, priority: PRIORITY.cue, gain: 0.55, rateSpread: 0.08, minInterval: 0.03,
    patch: {
      layers: [
        { wave: 'noise', decay: 0.05, curve: 3, gain: 1, filter: { type: 'bp', cutoff: 1400, cutoffEnd: 600, q: 1 } },
        { wave: 'sine', freq: 240, freqEnd: 110, decay: 0.07, gain: 0.8 },
        ...syllable({ at: 0.025, dur: 0.11, from: 200, to: 150, vowel: 'uh', attack: 0.005, breath: 0.3, gain: 0.7 }),
      ],
      jitter: { pitch: 0.08, time: 0.1 },
    },
  },
  enemyKilled: {
    variants: 3, priority: PRIORITY.cue, gain: 0.6, rateSpread: 0.08, minInterval: 0.03,
    patch: {
      layers: [
        { wave: 'noise', decay: 0.05, curve: 3, gain: 1, filter: { type: 'bp', cutoff: 1400, cutoffEnd: 600, q: 1 } },
        { wave: 'sine', freq: 240, freqEnd: 110, decay: 0.07, gain: 0.8 },
        ...syllable({ at: 0.03, dur: 0.34, from: 245, to: 118, vowel: 'ah', attack: 0.006, breath: 0.3, gain: 0.8 }),
        // The body going down.
        { wave: 'sine', at: 0.42, freq: 80, freqEnd: 36, sweep: 0.6, decay: 0.22, gain: 0.9 },
        { wave: 'noise', at: 0.42, decay: 0.17, curve: 2.5, gain: 0.7, filter: { type: 'lp', cutoff: 600 } },
      ],
      jitter: { pitch: 0.08, time: 0.1 },
    },
  },
  breathIn: {
    variants: 2, priority: PRIORITY.ambient, gain: 0.16,
    patch: {
      layers: [
        { wave: 'noise', attack: 0.22, decay: 0.26, gain: 1,
          filter: { type: 'bp', cutoff: 1100, cutoffEnd: 1700, q: 2.5 } },
      ],
      jitter: { pitch: 0.06, time: 0.1 },
    },
  },
  breathOut: {
    variants: 2, priority: PRIORITY.ambient, gain: 0.18,
    patch: {
      layers: [
        { wave: 'noise', attack: 0.1, decay: 0.5, gain: 1, filter: { type: 'bp', cutoff: 950, cutoffEnd: 560, q: 2 } },
      ],
      jitter: { pitch: 0.06, time: 0.1 },
    },
  },
  /** Three seconds of dressing a wound: packet torn, cloth wound round, a knot pulled tight. */
  firstAid: {
    variants: 1, priority: PRIORITY.detail, gain: 0.35,
    patch: {
      layers: [
        { wave: 'noise', at: 0.05, attack: 0.12, decay: 0.3, gain: 0.8, filter: { type: 'hp', cutoff: 3200 } },
        { wave: 'noise', at: 0.6, attack: 0.05, decay: 0.3, gain: 0.7, tremHz: 21, tremDepth: 0.7,
          filter: { type: 'bp', cutoff: 2600, q: 1 } },
        { wave: 'pink', at: 1.05, attack: 0.1, decay: 0.35, gain: 1, filter: { type: 'bp', cutoff: 1700, q: 0.8 } },
        { wave: 'noise', at: 1.6, attack: 0.06, decay: 0.3, gain: 0.7, tremHz: 17, tremDepth: 0.7,
          filter: { type: 'bp', cutoff: 3100, q: 1 } },
        { wave: 'pink', at: 2.1, attack: 0.1, decay: 0.3, gain: 0.9, filter: { type: 'bp', cutoff: 1500, q: 0.8 } },
        { wave: 'noise', at: 2.62, decay: 0.05, curve: 3, gain: 1, filter: { type: 'bp', cutoff: 1900, q: 2 } },
      ],
    },
  },
  heal: {
    variants: 1, priority: PRIORITY.action, gain: 0.3,
    patch: {
      layers: [
        { wave: 'triangle', freq: 523.25, attack: 0.01, hold: 0.06, decay: 0.12, gain: 1 },
        { wave: 'triangle', at: 0.11, freq: 659.26, attack: 0.01, hold: 0.08, decay: 0.3, gain: 1 },
      ],
    },
  },
  cloth: {
    variants: 2, priority: PRIORITY.detail, gain: 0.35,
    patch: {
      layers: [
        { wave: 'pink', attack: 0.08, decay: 0.25, gain: 1, filter: { type: 'bp', cutoff: 1600, q: 0.8 } },
        { wave: 'noise', at: 0.22, attack: 0.05, decay: 0.22, gain: 0.7, tremHz: 19, tremDepth: 0.6,
          filter: { type: 'bp', cutoff: 2800, q: 1 } },
      ],
      jitter: { pitch: 0.1, time: 0.1 },
    },
  },
  /** Kit going into webbing: a clink, a rattle, and a two-note "got it". */
  pickup: {
    variants: 2, priority: PRIORITY.action, gain: 0.4,
    patch: {
      layers: [
        { wave: 'sine', freq: 1800, fmRatio: 2.52, fmIndex: 1.8, fmDecay: 0.02, decay: 0.07, curve: 3, gain: 0.6 },
        { wave: 'noise', at: 0.03, decay: 0.09, gain: 0.5, tremHz: 45, tremDepth: 0.8,
          filter: { type: 'bp', cutoff: 2300, q: 1.5 } },
        { wave: 'square', duty: 0.25, at: 0.08, freq: 659.26, attack: 0.004, hold: 0.04, decay: 0.04, gain: 0.5,
          crush: 11025, filter: { type: 'lp', cutoff: 4200 } },
        { wave: 'square', duty: 0.25, at: 0.16, freq: 987.77, attack: 0.004, hold: 0.05, decay: 0.12, gain: 0.5,
          crush: 11025, filter: { type: 'lp', cutoff: 4200 } },
      ],
      jitter: { pitch: 0.02 },
    },
  },
  /** The charge set down and armed: a clunk, then two confirming beeps. */
  bombPlaced: {
    variants: 1, priority: PRIORITY.action, gain: 0.55,
    patch: {
      layers: [
        { wave: 'sine', freq: 155, freqEnd: 88, decay: 0.09, gain: 1 },
        { wave: 'noise', decay: 0.05, curve: 3, gain: 0.7, filter: { type: 'lp', cutoff: 1300 } },
        { wave: 'square', at: 0.26, freq: 1975.5, attack: 0.003, hold: 0.07, decay: 0.02, gain: 0.45, crush: 14000 },
        { wave: 'square', at: 0.4, freq: 1975.5, attack: 0.003, hold: 0.07, decay: 0.02, gain: 0.45, crush: 14000 },
      ],
    },
  },
  bombTick: {
    variants: 1, priority: PRIORITY.cue, gain: 0.4, maxDistance: 40,
    patch: {
      layers: [
        { wave: 'sine', freq: 1750, fmRatio: 1.41, fmIndex: 1.2, fmDecay: 0.006, decay: 0.035, curve: 3, gain: 1 },
        { wave: 'noise', decay: 0.008, curve: 3, gain: 0.5, filter: { type: 'hp', cutoff: 4000 } },
      ],
    },
  },
} satisfies Record<string, SfxDef>;
